import type { LocationPoint, Venue } from '../planning.ts'
import { plannerApiPayload, plannerApiUrl } from '../planner-api.ts'

export interface BikeShareStation {
  id: string
  name: string
  lat: number
  lon: number
  bikesAvailable: number
  docksAvailable: number
  isRenting: boolean
  isReturning: boolean
}

export interface BikeShareSnapshot {
  updatedAt: number
  stations: BikeShareStation[]
}

export async function bikeShareSnapshot(
  points: Array<Pick<LocationPoint | Venue, 'lat' | 'lon'>>,
): Promise<BikeShareSnapshot> {
  const snapshots: BikeShareSnapshot[] = []
  for (let offset = 0; offset < points.length; offset += 31) {
    snapshots.push(await requestBikeShareSnapshot(points.slice(offset, offset + 31)))
  }
  if (!snapshots.length) throw new Error('Bike-share station availability requires a location.')
  return {
    updatedAt: Math.min(...snapshots.map(({ updatedAt }) => updatedAt)),
    stations: [...new Map(snapshots.flatMap(({ stations }) =>
      stations.map((station) => [station.id, station] as const))).values()],
  }
}

async function requestBikeShareSnapshot(
  points: Array<Pick<LocationPoint | Venue, 'lat' | 'lon'>>,
): Promise<BikeShareSnapshot> {
  const query = new URLSearchParams({
    points: points.map(({ lat, lon }) => `${lat},${lon}`).join(';'),
  })
  const response = await fetch(plannerApiUrl(`/api/bike-share?${query}`))
  const payload = await plannerApiPayload<Partial<BikeShareSnapshot> & { error?: string }>(response)
  if (!response.ok) throw new Error(payload.error ?? 'Bike-share station availability failed')
  if (!Number.isInteger(payload.updatedAt) || !Array.isArray(payload.stations)) {
    throw new Error('Bike-share station availability returned an invalid result.')
  }
  if (!payload.stations.every(isBikeShareStation)) {
    throw new Error('Bike-share station availability returned an invalid station.')
  }
  return { updatedAt: payload.updatedAt!, stations: payload.stations }
}

function isBikeShareStation(value: unknown): value is BikeShareStation {
  if (!value || typeof value !== 'object') return false
  const station = value as Partial<BikeShareStation>
  return typeof station.id === 'string' && station.id.length >= 1 && station.id.length <= 100
    && typeof station.name === 'string' && station.name.length >= 1 && station.name.length <= 200
    && typeof station.lat === 'number' && Number.isFinite(station.lat)
    && typeof station.lon === 'number' && Number.isFinite(station.lon)
    && Number.isInteger(station.bikesAvailable) && station.bikesAvailable! >= 0
    && Number.isInteger(station.docksAvailable) && station.docksAvailable! >= 0
    && typeof station.isRenting === 'boolean' && typeof station.isReturning === 'boolean'
}
